<script setup lang="ts">
import { useRuntimeConfig } from '#imports'

const { t, locale } = useI18n()
const localeObject = useI18nLocaleObject()
const config = useRuntimeConfig()

const isValidTimeString = (time: string) => /^(?:[01]?\d|2[0-3]):[0-5]\d$/.test(time)

function parseFixedTime(time: string) {
  try {
    if (!time || !isValidTimeString(time)) {
      console.error('Invalid time format:', time)
      return new Date(Date.UTC(1970, 0, 1, 0, 0))
    }

    const [hours, minutes] = time.split(':').map(Number)

    if (hours && minutes && (hours < 0 || hours > 23 || minutes < 0 || minutes > 59)) {
      console.error('Invalid time:', hours, minutes)
      return new Date(Date.UTC(1970, 0, 1, 0, 0))
    }

    return new Date(Date.UTC(1970, 0, 1, hours, minutes))
  }
  catch (error) {
    console.error('Time parsing error:', error)
    return new Date(Date.UTC(1970, 0, 1, 0, 0))
  }
}

const serviceHours = computed(() => {
  const start = parseFixedTime(config.public.support.serviceHours.start)
  const end = parseFixedTime(config.public.support.serviceHours.end)

  const formatter = new Intl.DateTimeFormat(localeObject.value.language, {
    hour: 'numeric',
    minute: '2-digit',
    timeZone: 'UTC',
  })

  return formatter.formatRange(start, end)
})

const companyAddress = computed(() => {
  if (!config.public.company.address)
    return ''

  if (typeof config.public.company.address === 'string')
    return config.public.company.address

  return config.public.company.address[locale.value as keyof typeof config.public.company.address]
    ?? config.public.company.address[(Object.keys(config.public.company.address) as Array<keyof typeof config.public.company.address>)[0]!]
})
</script>

<template>
  <div class="relative flex items-center gap-x-4">
    <div class="h10 w10 flex shrink-0 grow-0 basis-auto items-center self-start justify-center rd-full bg-muted">
      <Icon name="lucide:map-pin" class="size-5 flex-none" />
    </div>

    <div class="text-base leading-6">
      <h3 class="text-sm text-primary fw600">
        {{ $config.public.company.name }}
      </h3>
      <p class="mt-6 text-sm text-primary fw600">
        {{ t('company.address.title') }}:
      </p>
      <p class="text-sm text-muted-foreground">
        {{ companyAddress }}
      </p>
      <div class="text-sm text-muted-foreground">
        <p>
          {{ t('company.id.title') }}: {{ $config.public.company.id }}
        </p>
        <p>
          {{ t('company.tax_id.title') }}: {{ $config.public.company.vatId }}
        </p>
      </div>
    </div>
  </div>

  <div class="relative mt4 flex items-center gap-x-4">
    <div class="h10 w10 flex shrink-0 grow-0 basis-auto items-center self-start justify-center rd-full bg-muted">
      <Icon name="lucide:mail" class="size-5 flex-none" />
    </div>

    <div class="text-base leading-6">
      <p class="text-sm text-primary fw600">
        {{ t('page.customer_service.title') }} {{ serviceHours }} {{ $config.public.support.serviceHours.timeZone }}
      </p>
      <a :href="`mailto:${$config.public.support.emailAddress}`" class="text-sm text-muted-foreground">
        {{ $config.public.support.emailAddress }}
      </a>
      <div class="c-slate500 dark:c-slate700" />
    </div>
  </div>
</template>
